/**
 * @requirement S-044 AC #1, #2, #3 — "ThemeProvider mounted at app root;
 *   theme class applied to `<html>` before first paint (no FOUC)",
 *   "System-following default when no persisted override", "Toggle in
 *   app-bar menu with three options: System / Light / Dark".
 *
 * ThemeProvider owns the resolved theme ('light' or 'dark') and the
 * user's preference ('system' | 'light' | 'dark'). It writes the
 * resolved theme to the `<html data-theme>` attribute so
 * `apps/web/src/styles/globals.css` `[data-theme='dark']` scoping
 * takes effect. When preference is 'system', it subscribes to
 * `matchMedia('prefers-color-scheme: dark')` and re-resolves on OS
 * change.
 *
 * FOUC prevention: the pre-paint inline script in `index.html` sets
 * `data-theme` before React mounts, so first paint always matches
 * the persisted preference. ThemeProvider then takes over on hydrate.
 */

import {
  type ReactElement,
  type ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { type ThemePreference, readPersistedTheme, writePersistedTheme } from './themePersistence';

export type ResolvedTheme = 'light' | 'dark';

export interface ThemeContextValue {
  /** User's stored preference — may be 'system'. */
  preference: ThemePreference;
  /** Effective theme applied to `<html>`. Always 'light' or 'dark'. */
  resolved: ResolvedTheme;
  /** Update the preference and persist it. */
  setPreference: (next: ThemePreference) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

const MEDIA_QUERY = '(prefers-color-scheme: dark)';

function resolveFromPreference(preference: ThemePreference): ResolvedTheme {
  if (preference === 'light' || preference === 'dark') return preference;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light';
}

function applyResolvedTheme(theme: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

export function ThemeProvider({ children }: { children: ReactNode }): ReactElement {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => readPersistedTheme() ?? 'system',
  );
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveFromPreference(preference));

  // Re-resolve + apply whenever preference changes.
  useEffect(() => {
    const next = resolveFromPreference(preference);
    setResolved(next);
    applyResolvedTheme(next);
  }, [preference]);

  // When preference is 'system', follow OS-level changes live.
  useEffect(() => {
    if (preference !== 'system') return undefined;
    if (typeof window === 'undefined') return undefined;
    const query = window.matchMedia(MEDIA_QUERY);
    const handler = (event: MediaQueryListEvent): void => {
      const next: ResolvedTheme = event.matches ? 'dark' : 'light';
      setResolved(next);
      applyResolvedTheme(next);
    };
    query.addEventListener('change', handler);
    return () => query.removeEventListener('change', handler);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    writePersistedTheme(next);
    setPreferenceState(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
